import type { GuestLinkDebugSnapshot, HardwareAccelerationKind, StreamDebugOverlaySnapshot } from '@playcastdotio/PeerCore';
import { formatGuestLinkDebugLine, formatStreamDebugOverlayLine } from '@playcastdotio/PeerCore';
import type { PeerStats } from '../../../coordinators/peer/managers/PeerStatsManager';

export type StreamDebugOverlayProps = {
  snapshot: StreamDebugOverlaySnapshot;
};

/**
 * Maps encode hardware status + outbound PeerStats into the Phase 1 overlay snapshot.
 * Prefer the peer with the highest outbound FPS when multiple peers are connected.
 */
export function buildHostStreamDebugSnapshot(
  encodeKind: HardwareAccelerationKind,
  peerStats: PeerStats[],
  encoderImplementation?: string,
  negotiatedCodec?: string,
  offeredProfiles?: string[],
  sendCodecPin?: string,
  /** Looks up the guest end of the link for the peer the overlay settled on. */
  guestLinkFor?: (peerId: string) => GuestLinkDebugSnapshot | undefined,
): StreamDebugOverlaySnapshot {
  const best = peerStats.reduce<PeerStats | undefined>((acc, stats) => {
    if (!acc) return stats;
    return (stats.framesPerSecond ?? 0) >= (acc.framesPerSecond ?? 0) ? stats : acc;
  }, undefined);

  const guestLink = best ? guestLinkFor?.(best.peerId) : undefined;
  return {
    codecKind: encodeKind,
    framesPerSecond: best?.framesPerSecond ?? 0,
    frameWidth: best?.frameWidth ?? 0,
    frameHeight: best?.frameHeight ?? 0,
    implementation: encoderImplementation,
    negotiatedCodec,
    offeredProfiles,
    sendCodecPin,
    ...(guestLink ? { guestLink } : {}),
  };
}

/**
 * Presentational host Beacon stream debug lines: the host's encode on the first, the guest's
 * end of the link (codec, jitter buffer, glass-to-glass) on the second once a guest reports.
 * Non-interactive — does not capture pointer events.
 */
export function StreamDebugOverlay({ snapshot }: StreamDebugOverlayProps) {
  return (
    <div
      className='pointer-events-none mb-2 font-mono text-xs text-emerald-300'
      data-testid='stream-debug-overlay'
      aria-hidden='true'
    >
      <div>{formatStreamDebugOverlayLine(snapshot)}</div>
      {snapshot.guestLink ? (
        <div data-testid='stream-debug-overlay-guest'>{formatGuestLinkDebugLine(snapshot.guestLink)}</div>
      ) : null}
    </div>
  );
}
